import React, { useState, useEffect } from 'react';
import { Cookie, Shield } from 'lucide-react';

const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setIsVisible(true), 1000);
    } else if (consent === 'accepted') {
      // Enable Google Analytics if previously accepted
      enableGoogleAnalytics();
    }
  }, []);

  const enableGoogleAnalytics = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const disableGoogleAnalytics = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_consent_date', new Date()?.toISOString());
    enableGoogleAnalytics();
    setIsVisible(false);
  };

  const handleRefuse = () => {
    localStorage.setItem('cookie_consent', 'refused');
    localStorage.setItem('cookie_consent_date', new Date()?.toISOString());
    disableGoogleAnalytics();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white shadow-2xl border-t-4 border-blue-600 animate-slide-up">
        <div className="max-w-7xl mx-auto p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Cookie className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Respect de votre vie privée
              </h3>
              <p className="text-gray-700 mb-3">
                Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic de notre site via Google Analytics. 
                Vos données sont traitées conformément au RGPD.
              </p>
              
              {showDetails && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-600">
                  <h4 className="font-semibold text-gray-900 mb-2">Détails des cookies :</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Google Analytics (G-SH2LN6W7DK)</strong> : Analyse du trafic et comportement des visiteurs</li>
                    <li><strong>Durée de conservation</strong> : 26 mois maximum</li>
                    <li><strong>Données collectées</strong> : Pages visitées, durée de visite, provenance</li>
                    <li><strong>Finalité</strong> : Amélioration de nos services</li>
                  </ul>
                  <p className="mt-3">
                    Vous pouvez modifier vos préférences à tout moment en cliquant sur le lien en bas de page.
                  </p>
                </div>
              )}

              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
              >
                {showDetails ? 'Masquer les détails' : 'En savoir plus'}
              </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={handleRefuse}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors duration-200"
              >
                Refuser
              </button>
              <button
                onClick={handleAccept}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg"
              >
                Accepter
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;